import { Grid, IconButton, Typography, useMediaQuery } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { observer } from "mobx-react";
import React, { useEffect } from "react";
import { BeatLoader } from "react-spinners";
import { LayoutDrawerStore } from "../../../stores/Layout/LayoutDrawerStore";
import theme from "../../../Theme/AppTheme";
import { isStringType } from "../../../utils/TypeGuards";
import { useStore } from "../../../utils/useStore";
import DoubleChevronSvg from "SvgIcons/DoubleChevronSvg";
import { styleClsx } from "utils/styleClsx";
import SmallScreenWarning from "components/Conversations/Views/Main/FocusedConversation/components/convoSidePanel/SmallScreenWarning";

interface OwnProps {
    width?: string; // default width 500px
    height?: string; // default height 100%
    title: React.ReactNode;
    subTitle?: React.ReactNode;
    titleIcon?: React.ReactNode;
    titleClass?: string;
    subtitleClass?: string;
    mainContentClass?: string;
    loading?: boolean;
    content: React.ReactNode;
    onClose?: () => void;
    anchor?: "left" | "right" | "top" | "bottom";
    contentStyles?: React.CSSProperties;
    titleStyles?: React.CSSProperties;
    titleTextStyle?: React.CSSProperties;
    subTitleStyles?: React.CSSProperties;
    subtitleTextStyle?: React.CSSProperties;
    keepContent?: boolean;
    customMarginTop?: string;
    wrapSubtitle?: boolean;
    arrowCloseIcon?: boolean;
    hideTitleBorder?: boolean;
    hideCloseIcon?: boolean;
    footerButtons?: React.ReactNode;
}

type Props = OwnProps;

const VerticalDrawerContentTemplate: React.FC<Props> = observer((props) => {
    const drawerStore = useStore(LayoutDrawerStore);

    const isXl = useMediaQuery(theme.breakpoints.up("xl"));
    const isMd = useMediaQuery(theme.breakpoints.up("lg"));

    const isScreenTooSmall = useMediaQuery("(max-width: 880px)");

    useEffect(() => {
        if (isXl) {
            drawerStore.setWidth(400);
        } else if (isMd) {
            drawerStore.setWidth(350);
        } else {
            drawerStore.setWidth(250);
        }
    }, [isXl, isMd, drawerStore]);

    function onClose() {
        drawerStore.closeAndResetDrawer(props.keepContent);
        props.onClose?.();
    }

    function titleElement(
        title: React.ReactNode,
        textStyle?: React.CSSProperties,
        subtitle?: boolean,
    ) {
        if (isStringType(title)) {
            return (
                <Typography
                    component={"div"}
                    className={
                        subtitle ? props.subtitleClass : props.titleClass
                    }
                    variant={subtitle ? "subtitle2" : "body1"}
                    noWrap={subtitle && props.wrapSubtitle ? false : true}
                    sx={{
                        fontSize: "16px",
                        fontWeight: 600,
                        ...(textStyle ?? {}),
                    }}
                >
                    {title}
                </Typography>
            );
        } else {
            return <>{title}</>;
        }
    }

    return (
        <div
            style={{
                width: props.width ?? "500px",
                height: props.height ?? "100%",
                marginTop: props.customMarginTop ?? "",
                display: "flex",
                flexDirection: "column",
            }}
        >
            {isScreenTooSmall ? (
                <SmallScreenWarning />
            ) : (
                <>
                    <Grid
                        container
                        justifyContent={"flex-start"}
                        borderBottom={
                            props.hideTitleBorder
                                ? "none"
                                : `0.25px solid ${theme.palette.gray[300]}`
                        }
                        style={{
                            position: "sticky",
                            top: 0,
                            backgroundColor: theme.palette.white.main,
                            zIndex: theme.zIndex.appBar - 1,
                        }}
                    >
                        <Grid item xs={12} container>
                            <Grid
                                item
                                xs={12}
                                container
                                justifyContent={"space-between"}
                                alignItems={"center"}
                                wrap={"nowrap"}
                                style={
                                    props.titleStyles ?? {
                                        padding: theme.spacing(1),
                                    }
                                }
                            >
                                <Grid
                                    container
                                    item
                                    xs={props.hideCloseIcon ? 12 : 11}
                                    alignItems={"center"}
                                >
                                    {props.titleIcon && (
                                        <Grid item>{props.titleIcon}</Grid>
                                    )}

                                    <Grid item xs zeroMinWidth>
                                        {titleElement(
                                            props.title,
                                            props.titleTextStyle,
                                        )}
                                    </Grid>
                                </Grid>

                                {!props.hideCloseIcon && (
                                    <Grid
                                        item
                                        container
                                        xs={1}
                                        style={{
                                            alignContent: "center",
                                            justifyContent: "flex-end",
                                        }}
                                    >
                                        <IconButton
                                            onClick={onClose}
                                            size="large"
                                            style={{
                                                padding: 0,
                                                marginRight: "16px",
                                            }}
                                        >
                                            {props.arrowCloseIcon ? (
                                                <DoubleChevronSvg />
                                            ) : (
                                                <CloseIcon
                                                    fontSize={"medium"}
                                                />
                                            )}
                                        </IconButton>
                                    </Grid>
                                )}
                            </Grid>

                            {Boolean(props.subTitle || props.loading) && (
                                <Grid
                                    item
                                    xs={12}
                                    style={
                                        props.subTitleStyles ?? {
                                            padding:
                                                "0.5rem 0.25rem 0.5rem 0.75rem",
                                        }
                                    }
                                    zeroMinWidth
                                >
                                    {titleElement(
                                        props.subTitle,
                                        props.subtitleTextStyle,
                                        true,
                                    )}

                                    <>
                                        {props.loading && (
                                            <BeatLoader
                                                size={6}
                                                color={
                                                    theme.palette.secondary.main
                                                }
                                                loading={props.loading}
                                            />
                                        )}
                                    </>
                                </Grid>
                            )}
                        </Grid>
                    </Grid>
                    <div
                        className={props.mainContentClass}
                        style={styleClsx(
                            {
                                paddingLeft: theme.spacing(1),
                                paddingRight: theme.spacing(1),
                                width: "100%",
                            },
                            props.contentStyles ?? {
                                padding: "0.5rem 0.25rem 0.5rem 0.75rem",
                                height: "90%",
                            },
                        )}
                    >
                        {props.content}
                    </div>
                    {props.footerButtons && (
                        <Grid
                            container
                            item
                            xs={12}
                            justifyContent={"flex-end"}
                            padding={theme.spacing(1)}
                            paddingRight={theme.spacing(2)}
                            maxHeight={"60px"}
                            style={{
                                position: "sticky",
                                bottom: 0,
                                backgroundColor: theme.palette.white.main,
                                borderTop: `1px solid ${theme.palette.gray[200]}`,
                            }}
                        >
                            {props.footerButtons}
                        </Grid>
                    )}
                </>
            )}
        </div>
    );
});

export default VerticalDrawerContentTemplate;
